'use client';

import { Suspense, useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { Search, MapPin, Calendar, Building2, GraduationCap, Briefcase, Users, Filter, X, Home, Loader2 } from 'lucide-react';
import { formatLifeSpan, formatDate } from '@/lib/utils';

interface Memorial {
  id: string;
  slug: string;
  firstName: string | null;
  lastName: string | null;
  birthName: string | null;
  dateOfBirth: string | null;
  dateOfDeath: string | null;
  city: string | null;
  placeOfResidence: string | null;
  imagePath: string | null;
  institutions?: Array<{
    id: string;
    institutionId?: string;
    institution: {
      id: string;
      name: string;
      type: string;
    };
  }>;
}

function SearchContent() {
  const searchParams = useSearchParams();

  const [memorials, setMemorials] = useState<Memorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Initialize from URL parameters
  useEffect(() => {
    const q = searchParams.get('q');
    const institution = searchParams.get('institution');
    const city = searchParams.get('city');

    if (q) setQuery(q);
    if (institution) setSelectedInstitution(institution);
    if (city) setSelectedCity(city);
  }, [searchParams]);

  // Fetch memorials from database
  useEffect(() => {
    const fetchMemorials = async () => {
      try {
        const res = await fetch('/api/memorials');
        if (res.ok) {
          const data = await res.json();
          setMemorials(data);
        }
      } catch (err) {
        console.error('Error fetching memorials:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMemorials();
  }, []);

  // Get unique cities from memorials
  const cities = useMemo(() => {
    const citySet = new Set<string>();
    memorials.forEach(m => {
      if (m.city) citySet.add(m.city);
    });
    return Array.from(citySet).sort();
  }, [memorials]);

  // Get unique institutions from memorials
  const institutions = useMemo(() => {
    const instMap = new Map<string, { id: string; name: string; type: string; count: number }>();
    memorials.forEach(m => {
      m.institutions?.forEach(ic => {
        const existing = instMap.get(ic.institution.id);
        if (existing) {
          existing.count++;
        } else {
          instMap.set(ic.institution.id, {
            id: ic.institution.id,
            name: ic.institution.name,
            type: ic.institution.type,
            count: 1,
          });
        }
      });
    });
    return Array.from(instMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [memorials]);

  // Filter memorials
  const filteredMemorials = useMemo(() => {
    return memorials.filter(memorial => {
      // Text search
      if (query) {
        const q = query.toLowerCase();
        const nameMatch =
          memorial.firstName?.toLowerCase().includes(q) ||
          memorial.lastName?.toLowerCase().includes(q) ||
          memorial.birthName?.toLowerCase().includes(q);
        if (!nameMatch) return false;
      }

      // City filter
      if (selectedCity && memorial.city !== selectedCity) {
        return false;
      }

      // Institution filter
      if (selectedInstitution) {
        const hasInstitution = memorial.institutions?.some(
          ic => ic.institution.id === selectedInstitution
        );
        if (!hasInstitution) return false;
      }

      return true;
    });
  }, [memorials, query, selectedCity, selectedInstitution]);

  const clearFilters = () => {
    setQuery('');
    setSelectedCity('');
    setSelectedInstitution('');
  };

  const hasActiveFilters = query || selectedCity || selectedInstitution;

  const getInstitutionIcon = (type: string) => {
    const normalizedType = type.toLowerCase();
    switch (normalizedType) {
      case 'school':
      case 'university':
        return <GraduationCap className="w-4 h-4" />;
      case 'company':
        return <Briefcase className="w-4 h-4" />;
      case 'club':
        return <Users className="w-4 h-4" />;
      default:
        return <Building2 className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Search Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-memorial-border p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Name Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-memorial-text-secondary" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name suchen..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary focus:ring-2 focus:ring-memorial-primary/10"
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-memorial-border hover:bg-memorial-muted transition-colors"
          >
            <Filter className="w-5 h-5" />
            <span>Filter {hasActiveFilters && `(${[query, selectedCity, selectedInstitution].filter(Boolean).length})`}</span>
          </button>

          {/* Desktop Filters */}
          <div className="hidden md:flex gap-4">
            {/* City Filter */}
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary bg-white min-w-[180px]"
            >
              <option value="">Alle Orte</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            {/* Institution Filter */}
            <select
              value={selectedInstitution}
              onChange={(e) => setSelectedInstitution(e.target.value)}
              className="px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary bg-white min-w-[220px]"
            >
              <option value="">Alle Institutionen</option>
              {institutions.map(inst => (
                <option key={inst.id} value={inst.id}>{inst.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile Filters Dropdown */}
        {showFilters && (
          <div className="md:hidden mt-4 pt-4 border-t border-memorial-border space-y-4">
            <div>
              <label className="block text-sm font-medium text-memorial-text-secondary mb-2">Ort</label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary bg-white"
              >
                <option value="">Alle Orte</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-memorial-text-secondary mb-2">Institution</label>
              <select
                value={selectedInstitution}
                onChange={(e) => setSelectedInstitution(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-memorial-border focus:outline-none focus:border-memorial-primary bg-white"
              >
                <option value="">Alle Institutionen</option>
                {institutions.map(inst => (
                  <option key={inst.id} value={inst.id}>{inst.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-memorial-border flex flex-wrap items-center gap-2">
            <span className="text-sm text-memorial-text-secondary">Aktive Filter:</span>
            {query && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-memorial-muted rounded-full text-sm">
                Name: {query}
                <button onClick={() => setQuery('')} className="hover:text-memorial-primary">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedCity && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-memorial-muted rounded-full text-sm">
                <MapPin className="w-3 h-3" />
                {selectedCity}
                <button onClick={() => setSelectedCity('')} className="hover:text-memorial-primary">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {selectedInstitution && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-memorial-muted rounded-full text-sm">
                <Building2 className="w-3 h-3" />
                {institutions.find(i => i.id === selectedInstitution)?.name}
                <button onClick={() => setSelectedInstitution('')} className="hover:text-memorial-primary">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className="text-sm text-memorial-secondary hover:text-memorial-primary"
            >
              Alle löschen
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-memorial-text-secondary">
          {filteredMemorials.length} {filteredMemorials.length === 1 ? 'Gedenkseite gefunden' : 'Gedenkseiten gefunden'}
        </p>
      </div>

      {/* Results Grid */}
      {filteredMemorials.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMemorials.map((memorial) => {
            const fullName = `${memorial.firstName || ''} ${memorial.lastName || ''}`.trim();
            const lifeSpan = formatLifeSpan(memorial.dateOfBirth, memorial.dateOfDeath);

            return (
              <Link
                key={memorial.id}
                href={`/m/${memorial.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-memorial-border hover:shadow-lg transition-all duration-300"
              >
                {/* Image */}
                <div className="relative h-48 bg-memorial-muted">
                  {memorial.imagePath ? (
                    <Image
                      src={memorial.imagePath}
                      alt={fullName}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-4xl font-serif text-memorial-text-secondary">
                        {memorial.firstName?.charAt(0)}{memorial.lastName?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h2 className="font-serif text-xl">{fullName}</h2>
                    <p className="text-white/80 text-sm">{lifeSpan}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  {/* Location */}
                  {memorial.city && (
                    <div className="flex items-center gap-2 text-sm text-memorial-text-secondary mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>{memorial.placeOfResidence || memorial.city}</span>
                    </div>
                  )}

                  {/* Death date */}
                  {memorial.dateOfDeath && (
                    <div className="flex items-center gap-2 text-sm text-memorial-text-secondary mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>Verstorben am {formatDate(memorial.dateOfDeath)}</span>
                    </div>
                  )}

                  {/* Institution badges */}
                  {memorial.institutions && memorial.institutions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {memorial.institutions.slice(0, 2).map((ic) => (
                        <span
                          key={ic.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-memorial-muted rounded-full text-xs text-memorial-text-secondary"
                        >
                          {getInstitutionIcon(ic.institution.type)}
                          <span className="truncate max-w-[120px]">{ic.institution.name}</span>
                        </span>
                      ))}
                      {memorial.institutions.length > 2 && (
                        <span className="px-2 py-1 bg-memorial-muted rounded-full text-xs text-memorial-text-secondary">
                          +{memorial.institutions.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-memorial-muted flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-memorial-text-secondary" />
          </div>
          <h2 className="font-serif text-2xl text-memorial-primary mb-2">Keine Ergebnisse</h2>
          <p className="text-memorial-text-secondary mb-6">
            Es wurden keine Gedenkseiten mit diesen Filterkriterien gefunden.
          </p>
          <button
            onClick={clearFilters}
            className="px-6 py-3 bg-memorial-primary text-white rounded-full hover:bg-memorial-primary/90 transition-colors"
          >
            Filter zurücksetzen
          </button>
        </div>
      )}

      {/* Institution Quick Links */}
      {institutions.length > 0 && (
        <div className="mt-16">
          <h2 className="font-serif text-2xl text-memorial-primary mb-6">Nach Institution suchen</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {institutions.map((inst) => (
              <button
                key={inst.id}
                onClick={() => {
                  setSelectedInstitution(inst.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-memorial-border hover:shadow-md transition-all text-left"
              >
                <div className="w-12 h-12 rounded-full bg-memorial-muted flex items-center justify-center flex-shrink-0">
                  {getInstitutionIcon(inst.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-memorial-text-primary truncate">{inst.name}</h3>
                  <p className="text-sm text-memorial-text-secondary">
                    {inst.count} {inst.count === 1 ? 'Gedenkseite' : 'Gedenkseiten'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SearchLoading() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
      </div>
    </div>
  );
}

export default function SearchPage() {
  return (
    <main className="min-h-screen bg-memorial-background">
      {/* Navigation */}
      <nav className="bg-white border-b border-memorial-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="font-serif text-xl text-memorial-primary">
              Gedenkportal
            </Link>
            <Link
              href="/"
              className="text-memorial-text-secondary hover:text-memorial-primary transition-colors flex items-center gap-2"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Startseite</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-memorial-primary text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="font-serif text-3xl md:text-4xl">Gedenkseiten durchsuchen</h1>
          <p className="text-white/70 mt-2">
            Finden Sie Gedenkseiten von Angehörigen, Freunden oder ehemaligen Kollegen
          </p>
        </div>
      </header>

      <Suspense fallback={<SearchLoading />}>
        <SearchContent />
      </Suspense>
    </main>
  );
}
